class TabbedPanels extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.nav = this.querySelector('.tabbed-panels__nav');
    this.tabs = Array.from(this.querySelectorAll('.tabbed-panels__tab'));

    this.replaceGlobalBlockLabels();
    this.setupKeyboardNavigation();
    this.setupActiveClass();
    this.observeRadioChanges();
    this.checkUrlParameter();
    this.setupPopstateListener();
    this.initHashNavigation();
    this.initScrollbarWidth();
    this.initAjaxPanels();
  }

  disconnectedCallback() {
    // Clean up the popstate listener when component is removed
    window.removeEventListener('popstate', this.boundPopstateHandler);
    window.removeEventListener('resize', this.setScrollbarWidth);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  setupKeyboardNavigation() {
    if (this.tabs.length === 0) return;

    // Add keyboard event listeners to each tab
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('keydown', (e) => this.handleKeyDown(e, index));
    });

    // Find initially checked radio to set correct tabindex
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const checkedId = checkedRadio.id;
      const checkedIndex = this.tabs.findIndex(tab => tab.getAttribute('for') === checkedId);
      if (checkedIndex !== -1) {
        this.updateTabIndex(checkedIndex);
      }
    }
  }

  handleKeyDown(event, currentIndex) {
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;

      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;

      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        break;

      default:
        return;
    }

    // Activate the new tab
    this.activateTab(newIndex);
  }

  activateTab(index, updateUrl = true) {
    const tab = this.tabs[index];
    if (!tab) return;

    // Remove active class from all tabs
    this.tabs.forEach(t => t.classList.remove('active'));

    // Add active class to the new tab
    tab.classList.add('active');

    // Click the label to check the radio button
    tab.click();

    // Focus the tab
    tab.focus();

    // Update tabindex
    this.updateTabIndex(index);

    // Update ARIA attributes
    this.updateAriaAttributes(index);

    if (updateUrl) this.updateUrlParameter(index);
  }

  updateTabIndex(activeIndex) {
    this.currentIndex = activeIndex;
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('tabindex', index === activeIndex ? '0' : '-1');
      tab.classList.toggle('active', index === activeIndex);
    });
  }

  updateAriaAttributes(activeIndex) {
    this.tabs.forEach((tab, index) => {
      tab.setAttribute('aria-selected', index === activeIndex ? 'true' : 'false');
    });
  }

  setupActiveClass() {
    // Set initial active class based on checked radio
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      const checkedId = checkedRadio.id;
      const activeTab = this.querySelector(`label[for="${checkedId}"]`);
      if (activeTab) {
        activeTab.classList.add('active');
      }
    }
  }

  observeRadioChanges() {
    // Listen for radio button changes
    const radios = this.querySelectorAll('input[type="radio"]');
    radios.forEach((radio, index) => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          // Remove active class from all tabs
          // this.tabs.forEach(tab => tab.classList.remove('active'));

          // Add active class to the clicked tab
          // const activeTab = this.querySelector(`label[for="${e.target.id}"]`);
          // if (activeTab) {
            // activeTab.classList.add('active');
            // Update URL when user clicks a tab
            this.updateTabIndex(index);
            this.updateUrlParameter(index);
          // }
        }
      });
    });
  }

  checkUrlParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab_panel');

    if (tabParam !== null) {
      // Convert to 0-based index (URL uses 1-based: ?tab_panel=1 means first tab)
      const tabIndex = parseInt(tabParam, 10) - 1;

      if (tabIndex >= 0 && tabIndex < this.tabs.length) {
        // Activate the tab without updating URL (to avoid infinite loop)
        this.activateTab(tabIndex, false);
      }
    }
  }

  setupPopstateListener() {
    this.boundPopstateHandler = () => this.checkUrlParameter();
    window.addEventListener('popstate', this.boundPopstateHandler);
  }

  updateUrlParameter(index) {
    // Use 1-based index for URL (more user-friendly)
    const tabNumber = index + 1;
    const url = new URL(window.location);
    url.searchParams.set('tab_panel', tabNumber);

    // Update URL without page reload
    window.history.pushState({}, '', url);
  }

  replaceGlobalBlockLabels() {
    const tabPanelDatas = document.querySelectorAll('.tab-panel-data');
    if (tabPanelDatas.length === 0) return;

    const dataMap = {};
    tabPanelDatas.forEach(tabPanelData => {
      try {
        const data = JSON.parse(tabPanelData.textContent);
        dataMap[data.id] = data;
      } catch (e) {
        console.warn('Invalid tab-panel-data JSON', e);
      }
    });

    this.querySelectorAll('.tab-label').forEach(label => {
      const data = dataMap[label.dataset.tabId];
      if (!data) return;

      const textEl = label.querySelector('.tab-label__text');

      // Apply gap
      const gap = data.svg_text_gap ?? 8;
      label.style.gap = gap + 'px';

      // Update text label
      if (textEl && data.tab_label && data.tab_label.trim() !== '') {
        textEl.textContent = data.tab_label;
      }

      // Inject SVG
      if (data.enable_svg && data.svg_code && data.svg_code.trim() !== '') {
        let svgEl = label.querySelector('.tab-label__svg');
        if (!svgEl) {
          svgEl = document.createElement('span');
          svgEl.className = 'tab-label__svg';
          label.insertBefore(svgEl, textEl || label.firstChild);
        }
        svgEl.innerHTML = data.svg_code;

        const size = data.svg_size || 24;
        svgEl.style.width = size + 'px';
        svgEl.style.height = size + 'px';
        svgEl.style.display = 'flex';
        svgEl.style.flexShrink = '0';

        const innerSvg = svgEl.querySelector('svg');
        if (innerSvg) {
          innerSvg.style.width = '100%';
          innerSvg.style.height = '100%';
          innerSvg.removeAttribute('width');
          innerSvg.removeAttribute('height');
        }
      }
    });
  }

  initHashNavigation() {
    if (window.location.hash) {
      this.activateTabById(window.location.hash.slice(1));
    }

    // Single click listener - handles hash, scroll, and tabindex
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('for');
        history.replaceState(null, '', '#' + tabId);
        this.updateTabIndex(index);
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });
    });
  }

  activateTabById(tabId) {
    const input = this.querySelector('#' + tabId);
    if (!input) return;

    input.checked = true;

    const label = this.querySelector(`label[for="${tabId}"]`);
    if (this.nav && label) {
      this.nav.scrollLeft = label.offsetLeft - this.nav.offsetWidth / 2 + label.offsetWidth / 2;
    }
  }

  initScrollbarWidth() {
    if (window.__scrollbarWidthInitialized) return;
    window.__scrollbarWidthInitialized = true;

    this.setScrollbarWidth();
    window.addEventListener('resize', this.setScrollbarWidth);
    this.resizeObserver = new ResizeObserver(this.setScrollbarWidth);
    this.resizeObserver.observe(document.documentElement);
  }

  setScrollbarWidth = () => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
  }

  initAjaxPanels() {
    const ajaxPanels = this.querySelectorAll('.tabbed-panels__panel-ajax-content');
    if (ajaxPanels.length === 0) return;

    ajaxPanels.forEach(el => {
      // fetch immediately on page load (not on tab switch) so content is ready before the user clicks
      const url = el.dataset.ajaxUrl;
      fetch(url)
        .then(res => res.text())
        .then(html => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const content = doc.querySelector('main') || doc.querySelector('.page-content') || doc.body;
          el.innerHTML = content.innerHTML;
        })
        .catch(() => {
          el.innerHTML = '<p>Failed to load content.</p>';
        });
    });
  }
}

export default TabbedPanels;